/**
 * Token Storage Helper
 *
 * Decides where to store the authentication token based on cookie consent:
 * - If user accepted cookies: localStorage (persists after browser close)
 * - If user rejected cookies: sessionStorage (cleared when tab closes)
 */

const COOKIE_CONSENT_KEY = 'cookie_consent'

/**
 * Check if user has accepted cookies (analytics/marketing)
 * Necessary cookies are always allowed, but we use sessionStorage for privacy-conscious users
 */
export function hasAcceptedCookies() {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
  if (!consent) return false

  try {
    const settings = JSON.parse(consent)
    // If user accepted analytics or marketing, they're OK with localStorage
    return settings.analytics === true || settings.marketing === true
  } catch {
    return false
  }
}

/**
 * Get the appropriate storage based on cookie consent
 */
export function getTokenStorage() {
  return hasAcceptedCookies() ? localStorage : sessionStorage
}

/**
 * Save token to appropriate storage
 */
export function saveToken(token) {
  const storage = getTokenStorage()
  storage.setItem('token', token)

  // If using sessionStorage (rejected cookies), make sure localStorage doesn't have old token
  if (!hasAcceptedCookies()) {
    localStorage.removeItem('token')
  }
}

/**
 * Save user data to appropriate storage
 */
export function saveUser(user) {
  const storage = getTokenStorage()
  storage.setItem('user', JSON.stringify(user))

  if (!hasAcceptedCookies()) {
    localStorage.removeItem('user')
  }
}

/**
 * Get token from storage (checks sessionStorage first, then localStorage)
 */
export function getToken() {
  // Always check sessionStorage first (for impersonation or rejected cookies)
  const sessionToken = sessionStorage.getItem('token')
  if (sessionToken) return sessionToken

  // Then check localStorage
  return localStorage.getItem('token')
}

/**
 * Get user from storage
 */
export function getUser() {
  const sessionUser = sessionStorage.getItem('user')
  if (sessionUser) {
    try {
      return JSON.parse(sessionUser)
    } catch {
      // Invalid JSON
    }
  }

  const localUser = localStorage.getItem('user')
  if (localUser) {
    try {
      return JSON.parse(localUser)
    } catch {
      // Invalid JSON
    }
  }

  return null
}

/**
 * Clear all token storage
 */
export function clearTokenStorage() {
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('user')
  sessionStorage.removeItem('impersonated')
  localStorage.removeItem('token')
  localStorage.removeItem('user')
}

/**
 * Check if this is an impersonated session
 */
export function isImpersonatedSession() {
  return sessionStorage.getItem('impersonated') === 'true'
}

export default {
  hasAcceptedCookies,
  getTokenStorage,
  saveToken,
  saveUser,
  getToken,
  getUser,
  clearTokenStorage,
  isImpersonatedSession
}
